import React, { FC, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { connectNames, convertDate, dowloadDocument } from "helpers";
import { Link, useParams } from "react-router-dom";
import { ReactComponent as DownloadIcon } from "images/downloadArrowOnCircleFilled.svg";
import { ReactComponent as TickIcon } from "images/OrganizationTickIcon.svg";
import { ReactComponent as HistoryIcon } from "images/History.svg";
import { ReactComponent as ThreeDotsIcon } from "images/ThreeDots.svg";
import { ReactComponent as Flag } from "images/Flag.svg";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import { getDocumentList, getVersionList } from "toolkit/documents/categorization/categorization.thunks";
import { changeDetaildeMode, changeVersionMode } from "toolkit/documents/categorization/categorization.slice";
import { DocumentVersions } from "../../DocumentVersions/DocumentVersions";
import { ModalWindow } from "components/ModalWindow/ModalWindow";
import styles from "./Document.module.sass";
import cn from "classnames";

interface DocumentProps {
	document: any;
	i?: number;
	address?: boolean;
	flag?: boolean;
}

interface IModalWindow {
	content: React.ReactElement | null;
	visibility: boolean;
	width: string;
}

export const Document: FC<DocumentProps> = ({ document, i, address = false, flag = false }) => {
	const { organizationId, condition } = useParams();
	const { currentTab, detailedMode, versionMode } = useAppSelector((state) => state.categorization);
	const [menu, setMenu] = useState<boolean>(false);
	const [displayingAddresses, setDisplayingAddresses] = useState<boolean>(false);
	const menuRef = useRef(null);
	const dispatch = useAppDispatch();
	const [modalWindow, setModalWindow] = useState<IModalWindow>({
		content: null,
		visibility: false,
		width: "",
	});

	useOutsideAlerter(menuRef.current, () => setMenu(false));

	const openMenu = () => {
		if (menu) return;
		setTimeout(() => {
			setMenu((prev) => !prev);
		}, 1);
	};

	const downloadHandler = (name: string, type?: string, id?: number) => {
		if ((currentTab === "REGISTRY" || currentTab === "MODEL" || (currentTab === "RESULT" && !id)) && !detailedMode)
			return;
		dowloadDocument(`/organization/doc/${id || document.id}/categorization?type=${type || currentTab}`, "", "", name);
		setMenu(false);
	};

	const openVersions = (doc?: any) => {
		dispatch(changeVersionMode(true));
		openModalWindow(<DocumentVersions close={closeModalWindow} />, "biggest");
		dispatch(getVersionList({ docId: doc?.id || document?.id, type: currentTab, archived: condition === "archive" }));
	};

	const openModalWindow = (component: React.ReactElement, width: string) => {
		setModalWindow({ ...modalWindow, content: component, visibility: true, width });
		setMenu(false);
	};

	const closeModalWindow = () => {
		setModalWindow({ ...modalWindow, content: null, visibility: false, width: "default" });
	};

	const clickHandler = () => {
		if ((currentTab !== "REGISTRY" && currentTab !== "MODEL") || detailedMode) return;
		dispatch(changeDetaildeMode(true));
		dispatch(
			getDocumentList({
				organizationId: Number(organizationId),
				type: currentTab,
				archived: condition === "archive",
				objectId: document.id,
			}),
		);
	};

	const menuTemp = (setting: any[]) => (
		<>
			<button onClick={openMenu} className={styles["menu-btn"]}>
				<ThreeDotsIcon />
			</button>
			<div ref={menuRef} className={styles["menu-wrp"]}>
				{menu && (
					<div onClick={(e) => e.stopPropagation()} className={cn(styles.menu)}>
						{setting.map((item) => (
							<>
								{item && (
									<div onClick={item.handler} className={styles.btn}>
										<div className={styles["icon-wrp"]}>{item.icon}</div>
										<span className={styles["btn-txt"]}>{item.txt}</span>
									</div>
								)}
							</>
						))}
					</div>
				)}
			</div>
		</>
	);

	const tableBodyTypes = {
		name: (
			<td className={styles.cell}>
				<div className={cn(versionMode && styles["flag-wrp"])}>
					{flag && document?.actual && (
						<div>
							<Flag className={styles.green} width="16" height="17" />
						</div>
					)}
					<span onClick={() => downloadHandler(document?.name)} className={styles.name}>
						{document?.name}
					</span>
				</div>
			</td>
		),
		objectName: (
			<td className={styles.cell}>
				<div className={styles["flag-wrp"]}>
					<div>
						<Flag className={styles.green} width="16" height="17" />
					</div>
					<span className={styles.name}>{document?.objectName}</span>
				</div>
			</td>
		),
		uploadDate: <td className={styles.cell}>{convertDate(document?.uploadDate || "")}</td>,
		author: (
			<td className={styles.cell}>
				{document?.author?.id ? (
					<Link to={`/user/${document?.author?.id}`} className="link">
						{document.author && connectNames(document.author)}
					</Link>
				) : (
					"—"
				)}

				<button onClick={openMenu} className={styles["menu-btn"]}>
					<ThreeDotsIcon />
				</button>
				{menuTemp([
					{ txt: "Скачать", icon: <DownloadIcon />, handler: () => downloadHandler(document?.name) },
					!versionMode && {
						txt: "Версии док-та",
						icon: <HistoryIcon />,
						handler: openVersions,
					},
				])}
			</td>
		),
		number: <td className={styles.cell}>{i}</td>,
		tickNumber: (
			<td className={styles.cell}>
				<div className={styles["cell-tick"]}>
					{!address && (
						<>
							<input
								checked={displayingAddresses}
								className="input-type none"
								onChange={() => setDisplayingAddresses((prev) => !prev)}
								type="checkbox"
								id={String(i) || "1"}
							/>
							<label
								htmlFor={String(i) || "1"}
								className={`organizations-table-body-row-item-tick-wrp ${
									document?.addresses?.length > 0 ? "" : "tick-disabled"
								}`}>
								<TickIcon className={`organizations-table-body-row-item-tick `} />
							</label>
						</>
					)}
					<span>{i}</span>
				</div>
			</td>
		),
		code: <td className={styles.cell}>{document?.code}</td>,
		type: <td className={styles.cell}>{document?.type?.name}</td>,
		activityArea: <td className={styles.cell}>{document?.activityArea?.name}</td>,
		part: (
			<td className={styles.cell}>
				<span onClick={() => downloadHandler(document.part, "BUNDLE")} className={styles.name}>
					{document?.part}
				</span>
			</td>
		),
		date: <td className={styles.cell}>{convertDate(document?.date || "") || "—"}</td>,
		partAuthor: (
			<td className={styles.cell}>
				{document?.partAuthor?.id ? (
					<Link to={`/user/${document?.partAuthor?.id}`} className="link">
						{connectNames(document.partAuthor)}
					</Link>
				) : (
					"—"
				)}
			</td>
		),
		letter: (
			<td className={styles.cell}>
				<span onClick={() => downloadHandler(document.letter, "LETTER")} className={styles.name}>
					{document?.letter}
				</span>
			</td>
		),
		letterAuthor: (
			<td className={styles.cell}>
				{document?.letterAuthor?.id ? (
					<Link to={`/user/${document?.letterAuthor?.id}`} className="link">
						{connectNames(document?.letterAuthor)}
					</Link>
				) : (
					"—"
				)}
				{menuTemp([
					{ txt: "Скачать партию", icon: <DownloadIcon />, handler: () => downloadHandler(document.part, "BUNDLE") },
					{
						txt: "Скачать ответное письмо",
						icon: <DownloadIcon />,
						handler: () => downloadHandler(document?.letter, "LETTER"),
					},
				])}
			</td>
		),
		docs: (
			<>
				<td className={styles.cell}>
					{versionMode ? (
						<span onClick={() => downloadHandler(document.name, "RESULT", document?.id)} className={styles.name}>
							{document?.name}
						</span>
					) : (
						<div className={styles["cell-double"]}>
							<span
								onClick={() => downloadHandler(document?.docs?.[0]?.name, "RESULT", document?.docs?.[0]?.id)}
								className={styles.name}>
								{document?.docs?.[0]?.name || "—"}
							</span>
							<span
								onClick={() => downloadHandler(document?.docs?.[1]?.name, "RESULT", document?.docs?.[1]?.id)}
								className={styles.name}>
								{document?.docs?.[1]?.name || "—"}
							</span>
						</div>
					)}
				</td>
				<td className={styles.cell}>
					{versionMode ? (
						<span>{convertDate(document?.approvalDate || "") || "—"}</span>
					) : (
						<div className={styles["cell-double"]}>
							<span>{convertDate(document?.docs?.[0]?.approvalDate || "") || "—"}</span>
							<span>{convertDate(document?.docs?.[1]?.approvalDate || "") || "—"}</span>
						</div>
					)}
				</td>
				<td className={styles.cell}>
					{versionMode ? (
						<span>{convertDate(document?.uploadDate || "") || "—"}</span>
					) : (
						<div className={styles["cell-double"]}>
							<span>{convertDate(document?.docs?.[0]?.uploadDate || "") || "—"}</span>
							<span>{convertDate(document?.docs?.[1]?.uploadDate || "") || "—"}</span>
						</div>
					)}
				</td>
				<td className={styles.cell}>
					{!versionMode ? (
						<div className={styles["cell-double"]}>
							{document?.docs?.[0]?.author?.id ? (
								<span>
									<Link to={`/user/${document?.docs?.[0]?.author?.id}`} className="link">
										{connectNames(document?.docs?.[0]?.author)}
									</Link>
								</span>
							) : (
								"—"
							)}
							{document?.docs?.[1]?.author?.id ? (
								<span>
									<Link to={`/user/${document?.docs?.[1]?.author?.id}`} className="link">
										{connectNames(document?.docs?.[1]?.author)}
									</Link>
								</span>
							) : (
								"—"
							)}
						</div>
					) : (
						<Link to={`/user/${document?.author?.id}`} className="link">
							{connectNames(document?.author || {})}
						</Link>
					)}
					{menuTemp([
						versionMode && {
							txt: "Скачать",
							icon: <DownloadIcon />,
							handler: () => downloadHandler(document?.name, "RESULT", document?.id),
						},
						!versionMode && {
							txt: "Скачать документ 1",
							icon: <DownloadIcon />,
							handler: () => downloadHandler(document?.docs?.[0]?.name, "RESULT", document?.docs?.[0]?.id),
						},
						!versionMode && {
							txt: "Скачать документ 2",
							icon: <DownloadIcon />,
							handler: () => downloadHandler(document?.docs?.[1]?.name, "RESULT", document?.docs?.[1]?.id),
						},
						!versionMode && {
							txt: "Версии док-та 1",
							icon: <HistoryIcon />,
							handler: () => openVersions(document?.docs?.[0]),
						},
						!versionMode && {
							txt: "Версии док-та 2",
							icon: <HistoryIcon />,
							handler: () => openVersions(document?.docs?.[1]),
						},
					])}
				</td>
			</>
		),
	};

	const tableBodyCells = {
		ORDER: ["name", "uploadDate", "author"],
		PROCESS: ["name", "uploadDate", "author"],
		REGISTRY: ["number", "code", "name", "type", "activityArea"],
		MODEL: ["number", "code", "name", "type", "activityArea"],
		ENUM: ["name", "uploadDate", "author"],
		RESULT: [versionMode ? "number" : "tickNumber", versionMode ? "objectName" : "name", "docs"],
		BUNDLE: ["number", "part", "date", "partAuthor", "letter", "letterAuthor"],
	};

	const rowTypes = {
		ORDER: 1,
		PROCESS: 1,
		REGISTRY: 2,
		ENUM: 1,
		MODEL: 2,
		RESULT: 3,
		BUNDLE: 4,
	};

	type cellTypeKey = keyof typeof tableBodyCells;
	type bodyTypeKey = keyof typeof tableBodyTypes;
	type rowTypeKey = keyof typeof rowTypes;

	return (
		<>
			{modalWindow.visibility && (
				<ModalWindow close={closeModalWindow} width={modalWindow.width} padding={false}>
					{modalWindow.content}
				</ModalWindow>
			)}
			<tr
				onClick={clickHandler}
				className={cn(
					styles.row,
					address && styles.address,
					styles[`row-type-${detailedMode ? "1" : rowTypes[currentTab as rowTypeKey]}`],
				)}>
				{tableBodyCells?.[detailedMode ? ("ORDER" as cellTypeKey) : (currentTab as cellTypeKey)]?.map(
					(cell) => tableBodyTypes[cell as bodyTypeKey],
				)}
			</tr>
			{displayingAddresses &&
				document.addresses.map((item: any, i: number) => <Document document={item} i={i + 1} address={true} />)}
		</>
	);
};
